// src/pages/admin/DevicesPageList.tsx

import { Fragment, useState } from "react";
import { Container, Col, Row, Card, Table, Alert } from "react-bootstrap";
import useDevicesDataList, { DeviceDataItem } from "hooks/useDevicesDataList";
import AddDeviceModal from "components/admin/AddDeviceModal";
import EditDeviceModal from "components/admin/EditDeviceModal";
import EditDeviceParamsModal from "components/admin/EditDeviceParamsModal";
import { useDeviceMenuRefresh } from "context/DeviceMenuRefreshContext";
import ApiKeyModal from "components/admin/ApiKeyModal";
// import http from "services/http";
import http from "services/httpClient";


export const DevicesPageList = () => {
  const { triggerRefresh } = useDeviceMenuRefresh();
  const [refreshKey, setRefreshKey] = useState(0);
  const devices = useDevicesDataList(refreshKey);
  const [showModal, setShowModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<string>("success");
  const [editModalShow, setEditModalShow] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceDataItem | null>(null);
  const [paramModalShow, setParamModalShow] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [apiModalShow, setApiModalShow] = useState(false);
  const [apiDeviceId, setApiDeviceId] = useState<number | null>(null);

  

  const handleEditParams = (device: DeviceDataItem) => {
    setSelectedDeviceId(device.id);
    setParamModalShow(true);
  };

  const handleSaveDevice = async (deviceData: {
    device_name: string;
    client_id: number;
    conn: number;
    param_count: number;
  }) => {
    try {
      const deviceExists = devices.some(d => d.device_name === deviceData.device_name);
      if (deviceExists) {
        setAlertMessage("Device name already exists!");
        setAlertVariant("danger");
        return;
      }
      const { data: newDevice } = await http.post(`/device/create_full`, deviceData);
      setAlertMessage("New device created: " + JSON.stringify(newDevice));
      setAlertVariant("success");
      setRefreshKey((prev) => prev + 1);
      triggerRefresh();
    } catch (error) {
      setAlertMessage("Error creating device: " + error);
      setAlertVariant("danger");
    }
    setTimeout(() => setAlertMessage(null), 3000);
  };

  const handleDeleteDevice = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this device?")) return;

    try {
      await http.delete(`/device/${id}`);
      setAlertMessage("Device deleted successfully");
      setAlertVariant("success");
      setRefreshKey((prev) => prev + 1);
      triggerRefresh();
    } catch (err: any) {
      const backendMessage =
        err.response?.data?.detail || "Failed to delete device. Please try again.";
      setAlertMessage(backendMessage);
      setAlertVariant("danger");
    }

    setTimeout(() => setAlertMessage(null), 3000);
  };


  const handleEditDevice = (device: DeviceDataItem) => {
    setSelectedDevice(device);
    setEditModalShow(true);
  };

  const handleSaveDeviceEdit = async (
    id: number,
    device_name: string,
    client_id: number,
    conn: number,
    param_count: number
  ) => {
    const updateData = {
      device_name: device_name,
      client_id: client_id,
      conn: conn,
      param_count: param_count
    };

    // console.log("Sending PATCH data:", updateData);

    try {
      await http.patch(`/device/${id}`, updateData);
      setAlertMessage("Device updated successfully");
      setAlertVariant("success");
      setRefreshKey((prev) => prev + 1);
      triggerRefresh();
    } catch (err) {
      // console.error("Failed to update device:", err);
      setAlertMessage("Failed to update device. Please try again.");
      setAlertVariant("danger");
    }
    setTimeout(() => setAlertMessage(null), 3000);
  };


  const connLabel = (conn: number) => {
    if (conn === 1) return "FTP";
    if (conn === 2) return "MQTT";
    return "";
  };

  return (
    <Fragment>
      <div className="bg-danger pt-5 pb-21"></div>
      <Container fluid className="mt-n22 px-6">
        <Row className="mt-6">
          <Col md={12} xs={12}>
            <Card className="rounded-3 p-3 shadow-sm">
              <Card.Header className="bg-white py-4 d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Devices</h4>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowModal(true)}
                >
                  Add Device
                  <AddDeviceModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    onSave={handleSaveDevice}
                  />
                </button>
              </Card.Header>
              <Table responsive className="text-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Device Name</th>
                    <th>Client Name</th>
                    <th>User/Topic</th>
                    <th>Protocol</th>
                    <th>Param Count</th>
                    <th>Date Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device: DeviceDataItem) => (
                    <tr key={device.id}>
                      <td className="align-middle">{device.device_name}</td>
                      <td className="align-middle">{device.client_name}</td>
                      <td className="align-middle">{device.mount_point}</td>
                      <td className="align-middle">{connLabel(device.conn)}</td>
                      <td className="align-middle">{device.param_count}</td>
                      <td className="align-middle">
                        {new Date(device.date_created).toLocaleString()}
                      </td>
                      <td className="align-middle">
                        <button
                          className="btn btn-sm btn-outline-dark me-2"
                          onClick={() => {
                            setApiDeviceId(device.id);
                            setApiModalShow(true);
                          }}
                        >
                          API
                        </button>
                        <button
                          className="btn btn-sm btn-secondary me-2"
                          onClick={() => handleEditParams(device)}
                        >
                          Param
                        </button>

                        <button
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => handleEditDevice(device)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteDevice(device.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <EditDeviceParamsModal
                show={paramModalShow}
                onHide={() => setParamModalShow(false)}
                deviceId={selectedDeviceId!}
              />
              <EditDeviceModal
                show={editModalShow}
                onHide={() => setEditModalShow(false)}
                onSave={handleSaveDeviceEdit}
                device={selectedDevice}
              />
              <AddDeviceModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSave={handleSaveDevice}
              />
              <div>
                {alertMessage && (
                  <Alert
                    variant={alertVariant}
                    onClose={() => setAlertMessage(null)}
                    dismissible
                  >
                    {alertMessage}
                  </Alert>
                )}
              </div>
            </Card>
            {apiDeviceId !== null && (
              <ApiKeyModal
                show={apiModalShow}
                onHide={() => setApiModalShow(false)}
                deviceId={apiDeviceId}
              />
            )}
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default DevicesPageList;
